import { useQuery } from '@tanstack/react-query';
import type { DesktopDownload } from '@bidmanager/shared';
import { isWebLink, request } from '../api';

/**
 * The fixed link clients hand to new bidders: `/download`.
 *
 * Public, like the invite page — whoever opens it usually has no account yet.
 * A page rather than a redirect to the installer, so the bidder reads about the
 * Windows warning before meeting it, and so the link still says something
 * honest before any installer has been published.
 */
export function Download(): JSX.Element {
  const download = useQuery({
    queryKey: ['desktop-download'],
    queryFn: () => request<DesktopDownload>('GET', '/desktop/download'),
    retry: false,
  });
  const installer = download.data?.downloadUrl;

  return (
    <div className="card login">
      <h1 style={{ marginTop: 0, fontSize: 19 }}>BidManager desktop app</h1>

      {download.isLoading && <p className="muted small">Loading…</p>}

      {download.isError && (
        <p className="error small" style={{ marginBottom: 0 }}>
          The download could not be loaded. Try again in a moment.
        </p>
      )}

      {download.data && !isWebLink(installer) && (
        <p className="muted small" style={{ marginBottom: 0 }}>
          The desktop app is not available to download yet. Ask the person who invited you when it
          will be.
        </p>
      )}

      {download.data && isWebLink(installer) && (
        <>
          <p className="muted small" style={{ marginTop: 0 }}>
            Bidding happens in the desktop app. Install it, then sign in with the account from your
            invitation.
          </p>
          <a
            className="button primary"
            href={installer}
            style={{ display: 'block', textAlign: 'center' }}
          >
            Download for Windows
          </a>
          <ol className="small" style={{ paddingLeft: 18, marginBottom: 0 }}>
            <li>Open the downloaded installer.</li>
            <li>
              If Windows shows <em>Windows protected your PC</em>, click <strong>More info</strong>,
              then <strong>Run anyway</strong>.
            </li>
            <li>Start BidManager and sign in.</li>
          </ol>
        </>
      )}
    </div>
  );
}
